import React, { useEffect, useId, useRef, useState } from "react";
import type {
  VisitComparison,
  VisitComparisonInput,
  VisitQuery,
  VisitSnapshot,
  VisitsAccess,
} from "../shared/visits.ts";
import { api, Banner, Button, date, Field, Modal } from "./ui.tsx";
import { Count, Freshness } from "./visits-presentation.tsx";
import {
  formatVisitDifference,
  formatVisitNumber,
} from "./visits-view-model.ts";
import "./visits-compare.css";

type Props = { query: VisitQuery; access: VisitsAccess; onClose: () => void };
type Mode = "scopes" | "snapshots" | "periods";
type Dimension = "source" | "group" | "responsible";
type Compared = {
  data: VisitComparison;
  labels: { left: string; right: string };
  description: string;
};
const path = "/visits-overview";
const dimensionNames = {
  source: "Planilhas de origem",
  group: "Grupos",
  responsible: "Responsáveis atuais",
};
const metrics = [
  { key: "uniqueAddresses", name: "Endereços únicos" },
  { key: "visited", name: "Visitados" },
  { key: "pending", name: "Pendentes" },
  { key: "analysis", name: "Aguardando análise" },
  { key: "coverage", name: "Cobertura de visitas" },
] as const;
const cancelled = (error: unknown) =>
  error instanceof Error && error.name === "AbortError";
const failure = (error: unknown) =>
  error instanceof Error
    ? error.message
    : "Não foi possível comparar. Confira as referências e tente novamente.";
const civilDate = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value.split("-").reverse().join("/")
    : value;

function reducedEffects() {
  const preferences = (
    window as Window & { GabineteUX?: { effectsEnabled?: () => boolean } }
  ).GabineteUX;
  return (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    document.body.classList.contains("effects-off") ||
    preferences?.effectsEnabled?.() === false
  );
}

export function VisitsCompare({ query, access, onClose }: Props) {
  const [mode, setMode] = useState<Mode>("scopes"),
    [dimension, setDimension] = useState<Dimension>("source");
  const [left, setLeft] = useState(""),
    [right, setRight] = useState("");
  const [snapshots, setSnapshots] = useState<VisitSnapshot[]>([]),
    [snapshotsLoading, setSnapshotsLoading] = useState(false),
    [snapshotRetry, setSnapshotRetry] = useState(0);
  const [dates, setDates] = useState({
    leftFrom: "",
    leftTo: "",
    rightFrom: "",
    rightTo: "",
    type: "first_visit" as NonNullable<VisitQuery["dateType"]>,
  });
  const [result, setResult] = useState<Compared | null>(null),
    [selecting, setSelecting] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const request = useRef<AbortController | null>(null),
    resultHeading = useRef<HTMLHeadingElement>(null),
    selectionHeading = useRef<HTMLHeadingElement>(null),
    selectionFrame = useRef<number | null>(null);
  const id = useId(),
    scope = `visits-compare:${id}`,
    formId = `visits-compare-form-${id}`;
  const canCompare =
    access.permissions.access &&
    access.permissions.indicators &&
    access.permissions.compare;
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    request.current?.abort();
    setBusy(false);
    setResult(null);
    setSelecting(true);
    setError("");
    return () => request.current?.abort();
  }, [access.configVersion, canCompare, queryKey]);
  useEffect(
    () => () => {
      request.current?.abort();
      if (selectionFrame.current !== null)
        cancelAnimationFrame(selectionFrame.current);
      (
        window as Window & {
          GabineteNumbers?: { dispose: (scope: string) => void };
        }
      ).GabineteNumbers?.dispose(scope);
    },
    [scope],
  );
  useEffect(() => {
    if (mode !== "snapshots" || !canCompare) {
      setSnapshotsLoading(false);
      return;
    }
    const controller = new AbortController();
    setSnapshotsLoading(true);
    setSnapshots([]);
    setError("");
    api<VisitSnapshot[]>(
      path + "/snapshots",
      "GET",
      undefined,
      controller.signal,
    )
      .then((next) => {
        if (!controller.signal.aborted) setSnapshots(next);
      })
      .catch((error) => {
        if (!controller.signal.aborted && !cancelled(error))
          setError(failure(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setSnapshotsLoading(false);
      });
    return () => controller.abort();
  }, [mode, canCompare, snapshotRetry, access.configVersion]);
  useEffect(() => {
    if (!result || selecting) return;
    const frame = requestAnimationFrame(() => {
      resultHeading.current?.focus({ preventScroll: true });
      resultHeading.current?.scrollIntoView({
        block: "start",
        behavior: reducedEffects() ? "instant" : "smooth",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [result, selecting]);

  const options =
    mode === "snapshots"
      ? snapshots.map((snapshot) => ({
          id: snapshot.id,
          name: `${snapshot.name} · ${date(snapshot.at)}`,
        }))
      : dimension === "source"
        ? access.sources
        : dimension === "group"
          ? access.groups.filter((group) => !group.parentId)
          : Object.entries(access.responsibleNames).map(([id, name]) => ({
              id,
              name,
            }));
  const field: keyof VisitQuery =
    dimension === "source"
      ? "sourceId"
      : dimension === "group"
        ? "groupId"
        : "responsibleId";
  const incompatibleScope =
    mode === "scopes" && dimension === "source" && !!query.tabId
      ? "Remova o filtro de aba na visão geral para comparar planilhas diferentes."
      : mode === "scopes" &&
          dimension === "group" &&
          !!query.subgroupId &&
          query.subgroupId !== "__unmapped__"
        ? "Remova o filtro de subgrupo na visão geral para comparar grupos diferentes."
        : "";
  const complete =
    !incompatibleScope &&
    (mode === "periods"
      ? !!(
          dates.leftFrom &&
          dates.leftTo &&
          dates.rightFrom &&
          dates.rightTo
        ) &&
        dates.leftFrom <= dates.leftTo &&
        dates.rightFrom <= dates.rightTo
      : !!left &&
        !!right &&
        left !== right &&
        options.some((option) => option.id === left) &&
        options.some((option) => option.id === right));
  const close = () => {
    request.current?.abort();
    if (selectionFrame.current !== null)
      cancelAnimationFrame(selectionFrame.current);
    onClose();
  };
  const change = () => {
    setSelecting(true);
    setError("");
    if (selectionFrame.current !== null)
      cancelAnimationFrame(selectionFrame.current);
    selectionFrame.current = requestAnimationFrame(() => {
      selectionFrame.current = null;
      selectionHeading.current?.focus({ preventScroll: true });
      selectionHeading.current?.scrollIntoView({
        block: "start",
        behavior: reducedEffects() ? "instant" : "smooth",
      });
    });
  };

  async function compare(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canCompare || !complete || busy) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const body: VisitComparisonInput =
      mode === "snapshots"
        ? {
            left: query,
            right: query,
            leftSnapshotId: left,
            rightSnapshotId: right,
          }
        : mode === "periods"
          ? {
              left: {
                ...query,
                dateType: dates.type,
                dateFrom: dates.leftFrom,
                dateTo: dates.leftTo,
              },
              right: {
                ...query,
                dateType: dates.type,
                dateFrom: dates.rightFrom,
                dateTo: dates.rightTo,
              },
            }
          : {
              left: { ...query, [field]: left },
              right: { ...query, [field]: right },
            };
    const labels =
      mode === "periods"
        ? {
            left: `${civilDate(dates.leftFrom)} a ${civilDate(dates.leftTo)}`,
            right: `${civilDate(dates.rightFrom)} a ${civilDate(dates.rightTo)}`,
          }
        : {
            left: options.find((option) => option.id === left)!.name,
            right: options.find((option) => option.id === right)!.name,
          };
    const description =
      mode === "snapshots"
        ? "Retratos históricos"
        : mode === "periods"
          ? dates.type === "first_visit"
            ? "Períodos pela primeira visita conhecida"
            : "Períodos pela última visita conhecida"
          : dimensionNames[dimension];
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const data = await api<VisitComparison>(
        path + "/compare",
        "POST",
        body,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setResult({ data, labels, description });
      setSelecting(false);
    } catch (error) {
      if (!controller.signal.aborted && !cancelled(error))
        setError(failure(error));
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }

  return (
    <Modal
      title="Comparar resultados"
      onClose={close}
      wide
      mobileFullScreen
      className="vo-compare-dialog"
    >
      <div className="vc-scroll">
        {!canCompare ? (
          <Banner kind="danger">
            Seu acesso não permite consultar este comparativo.
          </Banner>
        ) : (
          <>
            {error && (
              <Banner kind="danger">
                {error} As escolhas foram preservadas.
                {mode === "snapshots" && selecting && (
                  <p>
                    <Button
                      onClick={() => setSnapshotRetry((value) => value + 1)}
                    >
                      Tentar consultar retratos novamente
                    </Button>
                  </p>
                )}
              </Banner>
            )}
            {selecting ? (
              <form id={formId} onSubmit={compare}>
                <h3 ref={selectionHeading} tabIndex={-1}>
                  Escolha as referências
                </h3>
                <fieldset disabled={busy} className="vc-fieldset">
                  <Field label="O que deseja comparar?">
                    <select
                      value={mode}
                      onChange={(event) => {
                        setMode(event.target.value as Mode);
                        setLeft("");
                        setRight("");
                        setError("");
                      }}
                    >
                      <option value="scopes">
                        Fontes, grupos ou responsáveis
                      </option>
                      <option value="snapshots">
                        Retratos históricos compatíveis
                      </option>
                      <option value="periods">
                        Períodos pelas datas conhecidas
                      </option>
                    </select>
                  </Field>
                  {mode === "scopes" && (
                    <Field label="Comparar por">
                      <select
                        value={dimension}
                        onChange={(event) => {
                          setDimension(event.target.value as Dimension);
                          setLeft("");
                          setRight("");
                          setError("");
                        }}
                      >
                        {Object.entries(dimensionNames)
                          .filter(
                            ([key]) =>
                              key !== "responsible" ||
                              Object.keys(access.responsibleNames).length > 0,
                          )
                          .map(([key, name]) => (
                            <option value={key} key={key}>
                              {name}
                            </option>
                          ))}
                      </select>
                    </Field>
                  )}
                  {incompatibleScope && (
                    <Banner kind="warn">
                      {incompatibleScope}
                      <p>
                        <Button onClick={close}>
                          Voltar e ajustar filtros
                        </Button>
                      </p>
                    </Banner>
                  )}
                  {mode === "scopes" && !options.length && (
                    <p className="vc-hint">
                      Nenhuma referência está disponível nesta opção. Confira as
                      fontes ou escolha outro agrupamento.
                    </p>
                  )}
                  {mode === "periods" ? (
                    <>
                      <Field label="Data utilizada">
                        <select
                          value={dates.type}
                          onChange={(event) =>
                            setDates((previous) => ({
                              ...previous,
                              type: event.target.value as NonNullable<
                                VisitQuery["dateType"]
                              >,
                            }))
                          }
                        >
                          <option value="first_visit">
                            Primeira visita conhecida
                          </option>
                          <option value="latest_visit">
                            Última visita conhecida
                          </option>
                        </select>
                      </Field>
                      <p className="vc-hint">
                        A data mais recente não reconstitui a primeira visita.
                      </p>
                      <div className="vc-selection-grid">
                        {(["left", "right"] as const).map((side) => (
                          <fieldset className="vc-period" key={side}>
                            <legend>
                              {side === "left"
                                ? "A · Primeiro período"
                                : "B · Segundo período"}
                            </legend>
                            {(["From", "To"] as const).map((part) => (
                              <Field
                                key={part}
                                label={part === "From" ? "De" : "Até"}
                              >
                                <input
                                  required
                                  type="date"
                                  value={dates[`${side}${part}`]}
                                  min={
                                    part === "To"
                                      ? dates[`${side}From`]
                                      : undefined
                                  }
                                  onChange={(event) =>
                                    setDates((previous) => ({
                                      ...previous,
                                      [`${side}${part}`]: event.target.value,
                                    }))
                                  }
                                />
                              </Field>
                            ))}
                          </fieldset>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      {mode === "snapshots" && snapshotsLoading && (
                        <p role="status">Consultando retratos disponíveis…</p>
                      )}
                      {mode === "snapshots" &&
                        !snapshotsLoading &&
                        !snapshots.length &&
                        !error && (
                          <p className="vc-hint">
                            Nenhum retrato compatível está disponível. Abas
                            históricas externas precisam de mapeamento e
                            validação.
                          </p>
                        )}
                      <div className="vc-selection-grid">
                        {(["left", "right"] as const).map((side) => (
                          <Field
                            key={side}
                            label={
                              side === "left"
                                ? "A · Primeira referência"
                                : "B · Segunda referência"
                            }
                          >
                            <select
                              required
                              value={side === "left" ? left : right}
                              onChange={(event) =>
                                (side === "left" ? setLeft : setRight)(
                                  event.target.value,
                                )
                              }
                              disabled={
                                (mode === "snapshots" && snapshotsLoading) ||
                                !!incompatibleScope
                              }
                            >
                              <option value="">Selecione</option>
                              {options.map((option) => (
                                <option key={option.id} value={option.id}>
                                  {option.name}
                                </option>
                              ))}
                            </select>
                          </Field>
                        ))}
                      </div>
                    </>
                  )}
                  <p className="vc-hint">
                    Os demais filtros da visão geral são mantidos.
                  </p>
                </fieldset>
              </form>
            ) : null}
            {busy && (
              <p className="vc-processing" role="status">
                Comparando as bases…
              </p>
            )}
            {result && !selecting && (
              <section
                className="vc-result"
                aria-labelledby={`vc-result-${id}`}
              >
                <h3 id={`vc-result-${id}`} ref={resultHeading} tabIndex={-1}>
                  Resultado da comparação
                </h3>
                <div className="vc-choice">
                  <span>{result.description}</span>
                  <Button onClick={change}>Alterar comparação</Button>
                </div>
                <p className="vc-disclaimer">
                  Situação das bases. A diferença não equivale a visitas
                  realizadas entre datas.
                </p>
                {(["left", "right"] as const).some(
                  (side) =>
                    result.data[side].freshness.state !== "ready" ||
                    result.data[side].metrics.provisional,
                ) && (
                  <p className="vc-caution" role="status">
                    {(["left", "right"] as const)
                      .map((side) => {
                        const summary = result.data[side];
                        const state = {
                          ready: summary.metrics.provisional
                            ? "indicadores provisórios"
                            : "dados atualizados",
                          stale: "dados anteriores",
                          partial: "base parcial",
                          unavailable: "dados indisponíveis",
                          updating: "atualização em andamento",
                        }[summary.freshness.state];
                        return `${side === "left" ? "A" : "B"}: ${state}`;
                      })
                      .join(" · ")}
                    .
                  </p>
                )}
                <div
                  className="vc-references"
                  aria-label="Referências comparadas"
                >
                  {(["left", "right"] as const).map((side) => (
                    <div key={side}>
                      <span className="vc-letter" aria-hidden="true">
                        {side === "left" ? "A" : "B"}
                      </span>
                      <p>
                        <small>Referência {side === "left" ? "A" : "B"}</small>
                        <strong>{result.labels[side]}</strong>
                      </p>
                    </div>
                  ))}
                </div>
                <div className="vc-indicators">
                  {metrics.map((metric) => {
                    const coverage = metric.key === "coverage";
                    return (
                      <article className="vc-indicator" key={metric.key}>
                        <h4>{metric.name}</h4>
                        <div className="vc-pair">
                          {(["left", "right"] as const).map((side) => {
                            const value = result.data[side].metrics[metric.key];
                            return (
                              <div
                                className="vc-value"
                                key={side}
                                aria-label={`${side === "left" ? "A" : "B"} · ${result.labels[side]}`}
                              >
                                <span
                                  className="vc-value-reference"
                                  aria-hidden="true"
                                >
                                  {side === "left" ? "A" : "B"}
                                  {" · "}
                                  {result.labels[side]}
                                </span>
                                {value == null ? (
                                  <span className="vc-unavailable">
                                    {formatVisitNumber(value)}
                                  </span>
                                ) : (
                                  <>
                                    <strong>
                                      <Count
                                        value={value}
                                        decimals={coverage ? 1 : 0}
                                        scope={scope}
                                        label={`${side === "left" ? "A" : "B"}: ${metric.name}${coverage ? " em porcentagem" : " em endereços"}`}
                                      />
                                      {coverage && (
                                        <span aria-hidden="true">%</span>
                                      )}
                                    </strong>
                                    {!coverage && (
                                      <small>
                                        {value === 1 ? "endereço" : "endereços"}
                                      </small>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className="vc-difference">
                          <span>B em relação a A</span>
                          <strong>
                            {formatVisitDifference(
                              result.data.differences[metric.key],
                              coverage,
                            )}
                          </strong>
                        </p>
                      </article>
                    );
                  })}
                </div>
                <details className="vc-details">
                  <summary>Atualização e alcance dos dados</summary>
                  <div className="vc-selection-grid">
                    {(["left", "right"] as const).map((side) => (
                      <section key={side}>
                        <h4>
                          {side === "left" ? "Referência A" : "Referência B"}
                        </h4>
                        <Freshness
                          value={result.data[side].freshness}
                          access={access}
                        />
                      </section>
                    ))}
                  </div>
                </details>
                <details className="vc-details">
                  <summary>Como interpretar a comparação</summary>
                  <p>{result.data.explanation}</p>
                  <p>
                    A diferença corresponde a B menos A. Contagens usam
                    endereços; cobertura usa pontos percentuais (p.p.), sem
                    calcular crescimento relativo.
                  </p>
                  <p>
                    Cobertura é apresentada com uma casa decimal; diferenças
                    pequenas podem exigir mais precisão. Dado indisponível não é
                    zero.
                  </p>
                  {(["left", "right"] as const).map((side) => (
                    <section key={side}>
                      <h4>
                        Método da referência {side === "left" ? "A" : "B"}
                      </h4>
                      <p>{result.data[side].metrics.method}</p>
                      {result.data[side].metrics.provisional && (
                        <p>Esta referência contém indicadores provisórios.</p>
                      )}
                      <small>
                        Data de referência:{" "}
                        {date(result.data[side].freshness.asOf)}
                      </small>
                    </section>
                  ))}
                </details>
              </section>
            )}
          </>
        )}
      </div>
      <div className="vc-footer">
        {selecting && canCompare ? (
          <>
            <Button
              type="submit"
              form={formId}
              kind="primary"
              busy={busy}
              disabled={!complete}
            >
              Comparar
            </Button>
            {result && (
              <Button
                onClick={() => {
                  setSelecting(false);
                  setError("");
                }}
              >
                Voltar ao resultado
              </Button>
            )}
            <Button onClick={close}>Fechar</Button>
          </>
        ) : (
          <Button kind="primary" onClick={close}>
            Fechar comparação
          </Button>
        )}
      </div>
    </Modal>
  );
}

export default VisitsCompare;
